import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { wishlistAPI } from "../../services/api";

export const fetchWishlist = createAsyncThunk(
  "wishlist/fetchWishlist",
  async (_, { rejectWithValue }) => {
    try {
      const response = await wishlistAPI.getWishlist();
      return response.data; // Expected to be an array of products
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch wishlist");
    }
  }
);

export const toggleWishlist = createAsyncThunk(
  "wishlist/toggleWishlist",
  async ({ productId, isInWishlist }, { rejectWithValue, dispatch }) => {
    try {
      if (isInWishlist) {
        await wishlistAPI.removeFromWishlist(productId);
        return { productId, removed: true };
      } else {
        const response = await wishlistAPI.addToWishlist(productId);
        return { product: response.data, removed: false };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update wishlist");
    }
  }
);

const getInitialWishlist = () => {
  try {
    const savedIds = localStorage.getItem('wishlist_ids');
    const savedItems = localStorage.getItem('wishlist_items');
    return {
      ids: savedIds ? JSON.parse(savedIds) : [],
      items: savedItems ? JSON.parse(savedItems) : []
    };
  } catch (e) {
    console.error("Failed to load wishlist from storage", e);
    return { ids: [], items: [] };
  }
};

const savedWishlist = getInitialWishlist();

const initialState = {
  items: savedWishlist.items,
  wishlistIds: savedWishlist.ids,
  loading: false,
  error: null,
};

const wishListSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    syncWishlistIds: (state, action) => {
      state.wishlistIds = action.payload || [];
      localStorage.setItem('wishlist_ids', JSON.stringify(state.wishlistIds));
    },
    clearWishList: (state) => {
      state.items = [];
      state.wishlistIds = [];
      localStorage.removeItem('wishlist_ids');
      localStorage.removeItem('wishlist_items');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        // The API returns an array of objects wrapping the Product: [{ id, Product: { ... } }]
        // We map this to just store functionality relevant product objects
        const validItems = action.payload.filter(item => item.Product);
        state.items = validItems.map(item => item.Product);
        state.wishlistIds = validItems.map(item => item.Product.id);

        localStorage.setItem('wishlist_ids', JSON.stringify(state.wishlistIds));
        localStorage.setItem('wishlist_items', JSON.stringify(state.items));
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(toggleWishlist.fulfilled, (state, action) => {
        const { productId, removed, product } = action.payload;
        if (removed) {
          state.items = state.items.filter(item => item.id !== productId);
          state.wishlistIds = state.wishlistIds.filter(id => id !== productId);
        } else {
          // If we got response back, it might be { id, Product: {...} } or just Product
          // We normalize it to just the Product object
          const productToAdd = product?.Product || product;

          if (productToAdd && !state.items.find(item => item.id === productToAdd.id)) {
            state.items.push(productToAdd);
          }
          if (!state.wishlistIds.includes(productId)) {
            state.wishlistIds.push(productId);
          }
        }

        localStorage.setItem('wishlist_ids', JSON.stringify(state.wishlistIds));
        localStorage.setItem('wishlist_items', JSON.stringify(state.items));
      })
      .addMatcher(
        (action) => action.type === 'auth/logout',
        (state) => {
          state.items = [];
          state.wishlistIds = [];
          localStorage.removeItem('wishlist_ids');
          localStorage.removeItem('wishlist_items');
        }
      );
  },
});

export const { syncWishlistIds, clearWishList } = wishListSlice.actions;

export default wishListSlice.reducer;
